import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, X, FileText } from "lucide-react";
import api from "@/lib/api";

const SCREEN_LABELS = {
  qualificacao_inicial: "Qualificação Inicial",
  qualificacao_tecnica: "Reunião Técnica",
  beneficiario: "Beneficiários",
  plano_cuidados: "Plano de Cuidados",
};

const TYPE_LABELS = {
  text: "Texto curto",
  textarea: "Texto longo",
  number: "Número",
  date: "Data",
  boolean: "Sim / Não",
  select: "Dropdown",
  multiselect: "Múltipla escolha",
  checkboxes: "Checkboxes",
  file: "Ficheiro",
};

const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30";
const btn = "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-all";

function OptionsEditor({ options, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...(options || []), { value: draft.trim(), label: draft.trim() }]);
    setDraft("");
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Opções</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {(options || []).map((o, i) => (
          <span key={i} className="inline-flex items-center gap-2 bg-muted rounded-full pl-3 pr-1.5 py-1 text-xs">
            {o.label}
            <button type="button" className="text-muted-foreground/70 hover:text-[hsl(var(--danger))]"
              onClick={() => onChange(options.filter((_, j) => j !== i))} data-testid={`opt-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={inp} placeholder="Nova opção" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="opt-input" />
        <button type="button" onClick={add} className={`${btn} bg-muted hover:bg-muted border border-border text-muted-foreground`} data-testid="opt-add">
          <Plus className="w-3.5 h-3.5" /> Adicionar
        </button>
      </div>
    </div>
  );
}

function FieldEditor({ initial, screen, onSaved, onCancel }) {
  const isNew = !initial?.id;
  const [f, setF] = useState(initial || {
    label: "", type: "text", section: "", section_label: "",
    required: false, placeholder: "", help_text: "", options: [], show_in_pdf: false,
    max_length: null,
  });
  const [saving, setSaving] = useState(false);
  const needsOptions = ["select", "multiselect", "checkboxes"].includes(f.type);

  const save = async () => {
    if (!f.label?.trim()) { toast.error("O rótulo é obrigatório"); return; }
    if (needsOptions && (!f.options || f.options.length === 0)) { toast.error("Adicione pelo menos uma opção"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/form-fields", { ...f, screen });
        toast.success("Campo criado");
        onSaved(r.data);
      } else {
        const payload = {
          label: f.label, type: f.type, section: f.section || null, section_label: f.section_label || null,
          required: f.required, placeholder: f.placeholder || null, help_text: f.help_text || null,
          options: f.options, show_in_pdf: f.show_in_pdf, max_length: f.max_length || null,
        };
        const r = await api.put(`/form-fields/${f.id}`, payload);
        toast.success("Campo atualizado");
        onSaved(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar campo");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-5" data-testid="field-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-ui text-lg text-foreground">{isNew ? "Novo campo" : `Editar: ${initial.label}`}</h3>
        {f.is_standard && <span className="text-[10px] uppercase tracking-[0.15em] text-foreground">Campo standard</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Rótulo *</div>
          <input className={inp} value={f.label || ""} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="fld-label" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Tipo</div>
          <select className={inp} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
            disabled={f.is_standard} data-testid="fld-type">
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Secção (chave)</div>
          <input className={inp} placeholder="contact, clinical..." value={f.section || ""} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="fld-section" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Secção (rótulo)</div>
          <input className={inp} placeholder="Contacto" value={f.section_label || ""} onChange={(e) => setF({ ...f, section_label: e.target.value })} />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Placeholder</div>
          <input className={inp} value={f.placeholder || ""} onChange={(e) => setF({ ...f, placeholder: e.target.value })} />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Texto de ajuda</div>
          <input className={inp} value={f.help_text || ""} onChange={(e) => setF({ ...f, help_text: e.target.value })} />
        </label>
      </div>

      {needsOptions && (
        <OptionsEditor options={f.options || []} onChange={(o) => setF({ ...f, options: o })} />
      )}

      <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-border/60">
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" className="accent-[#1D4ED8]" checked={!!f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} data-testid="fld-required" />
          Obrigatório
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" className="accent-[#1D4ED8]" checked={!!f.show_in_pdf} onChange={(e) => setF({ ...f, show_in_pdf: e.target.checked })} data-testid="fld-pdf" />
          <FileText className="w-3.5 h-3.5" /> Mostrar no PDF
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border/60">
        <button type="button" onClick={onCancel} className={`${btn} text-muted-foreground hover:text-foreground`}>Cancelar</button>
        <button type="button" onClick={save} disabled={saving} className={`${btn} bg-foreground hover:bg-slate-800 text-background disabled:opacity-60`} data-testid="fld-save">
          <Save className="w-3.5 h-3.5" /> {saving ? "A guardar…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

export default function FieldsSettings() {
  const [screen, setScreen] = useState("qualificacao_inicial");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // field object or "new"

  const load = async (s = screen) => {
    setLoading(true);
    try {
      const r = await api.get(`/form-fields?screen=${s}`);
      setFields(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(screen); /* eslint-disable-next-line */ }, [screen]);

  const toggleActive = async (f) => {
    try {
      const r = await api.put(`/form-fields/${f.id}`, { active: !f.active });
      setFields(prev => prev.map(x => x.id === f.id ? r.data : x));
      toast.success(!f.active ? "Campo ativado" : "Campo desativado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (f) => {
    if (f.is_standard) { toast.error("Campos standard não podem ser eliminados. Desative em alternativa."); return; }
    if (!window.confirm(`Eliminar o campo "${f.label}"?`)) return;
    try {
      await api.delete(`/form-fields/${f.id}`);
      setFields(prev => prev.filter(x => x.id !== f.id));
      toast.success("Campo eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const move = async (f, dir) => {
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(x => x.id === f.id);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
    setFields(sorted);
    try {
      await api.post("/form-fields/reorder", { ordered_ids: sorted.map(x => x.id) });
    } catch (e) { toast.error("Erro ao reordenar"); load(); }
  };

  const bySection = useMemo(() => {
    const g = {};
    [...fields].sort((a, b) => a.order - b.order).forEach(f => {
      const k = f.section || "_default";
      if (!g[k]) g[k] = { label: f.section_label || (k === "_default" ? "Outros" : k), items: [] };
      g[k].items.push(f);
    });
    return g;
  }, [fields]);

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="fields-root">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Administração · Configuração</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground">Campos dos formulários</h1>
          <p className="text-muted-foreground text-sm mt-2">Ative, desative, edite ou adicione campos personalizados a cada ecrã.</p>
        </div>
        <button onClick={() => setEditing("new")} className={`${btn} bg-foreground hover:bg-slate-800 text-background`} data-testid="add-field">
          <Plus className="w-4 h-4" /> Novo campo
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-8 bg-card border border-border rounded-full p-1 w-fit">
        {Object.entries(SCREEN_LABELS).map(([k, label]) => (
          <button key={k} onClick={() => setScreen(k)} data-testid={`screen-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
              screen === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {editing && (
        <div className="mb-6">
          <FieldEditor
            initial={editing === "new" ? null : editing}
            screen={screen}
            onSaved={(saved) => {
              setFields(prev => {
                const exists = prev.some(x => x.id === saved.id);
                if (exists) return prev.map(x => x.id === saved.id ? saved : x);
                return [...prev, saved];
              });
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {loading && <div className="text-sm text-muted-foreground">A carregar…</div>}

      {!loading && fields.length === 0 && (
        <div className="text-sm text-muted-foreground bg-card border border-dashed border-border rounded-2xl p-10 text-center">
          Ainda não existem campos para este ecrã.
        </div>
      )}

      {!loading && Object.entries(bySection).map(([sec, g]) => (
        <div key={sec} className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold mb-3">{g.label}</div>
          <div className="bg-card border border-border rounded-2xl divide-y divide-[#F1F5F9]">
            {g.items.map((f, i) => (
              <div key={f.id} className={`flex items-center gap-4 px-5 py-3 ${!f.active ? "opacity-55" : ""}`} data-testid={`field-row-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{f.label}</span>
                    <span className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground/70">· {TYPE_LABELS[f.type]}</span>
                    {f.is_standard && <span className="text-[9px] uppercase tracking-[0.15em] text-foreground">· standard</span>}
                    {f.required && <span className="text-[9px] uppercase tracking-[0.15em] text-[hsl(var(--warning))]">· obrigatório</span>}
                    {f.show_in_pdf && <span className="text-[9px] uppercase tracking-[0.15em] text-[#059669]">· PDF</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{f.key}{(f.options || []).length ? ` · ${f.options.length} opções` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(f, "up")} disabled={i === 0} className="p-2 rounded-lg text-muted-foreground/70 hover:bg-muted hover:text-foreground disabled:opacity-30" data-testid={`move-up-${f.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(f, "down")} disabled={i === g.items.length - 1} className="p-2 rounded-lg text-muted-foreground/70 hover:bg-muted hover:text-foreground disabled:opacity-30" data-testid={`move-down-${f.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleActive(f)} className="p-2 rounded-lg text-muted-foreground/70 hover:bg-muted hover:text-foreground" data-testid={`toggle-${f.id}`}>
                    {f.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => setEditing(f)} className="text-xs text-foreground hover:text-foreground px-2" data-testid={`edit-${f.id}`}>Editar</button>
                  {!f.is_standard && (
                    <button onClick={() => remove(f)} className="p-2 rounded-lg text-muted-foreground/70 hover:bg-[hsl(var(--danger-bg))] hover:text-[hsl(var(--danger))]" data-testid={`delete-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
